import React, { useEffect, useState, useCallback } from 'react';
import { to12h } from '../lib/timeFormat';
import { TimePicker12 } from '../components/TimePicker12';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { GraduationCap, ArrowLeft, MapPin, Video, Clock, User, Users, Send, Pencil, FileDown, Star, Flag, CalendarClock, Eye, Download, CheckCircle2, Circle, ChevronsUpDown, Check } from 'lucide-react';
import { indStatusClass } from './InductionsPage';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HR_DEPTS = ['Human Resources', 'Human Resources Management'];

const approvalClass = (s) => s === 'Accepted' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
  : s === 'Rejected' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
  : s === 'Reschedule Requested' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function InductionDetailPage() {
  const { getAuthHeader, employee } = useAuth();
  const navigate = useNavigate();
  const { inductionId } = useParams();
  const [ind, setInd] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resch, setResch] = useState(null); // session being rescheduled
  const [rForm, setRForm] = useState({ date: '', start_time: '', end_time: '', owner_id: '', owner_name: '', owner_email: '' });
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [ownerCandidates, setOwnerCandidates] = useState([]);
  const [viewFb, setViewFb] = useState(null);
  const [viewJoiners, setViewJoiners] = useState(null);
  const [conductNc, setConductNc] = useState(null); // session for Not-Conducted reason
  const [ncText, setNcText] = useState('');

  const isHR = employee?.role === 'Super Admin' || HR_DEPTS.includes(employee?.department);
  const MGMT_DEPTS = ['Management', 'Management and Strategy'];
  const canViewFeedback = isHR || MGMT_DEPTS.includes(employee?.department) || ['Management', 'Admin Head'].includes(employee?.role);

  const fmtSuggested = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    let h = d.getHours(); const min = String(d.getMinutes()).padStart(2, '0'); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${h}:${min} ${ap}`;
  };

  const sessionStatus = (s) => {
    if (s.approval_status !== 'Accepted') return s.approval_status;
    if (s.conduct_status === 'Not Conducted') return 'Not Conducted';
    if (s.conduct_status === 'Conducted') return s.completed ? 'Completed' : 'In Progress';
    return 'Incomplete';
  };
  const statusClass = (st) => ({
    'Completed': 'bg-violet-600 text-white hover:bg-violet-600',
    'Not Conducted': 'bg-rose-600 text-white hover:bg-rose-600',
    'In Progress': 'bg-amber-500 text-white hover:bg-amber-500',
    'Incomplete': 'bg-slate-400 text-white hover:bg-slate-400',
    'Accepted': 'bg-emerald-600 text-white hover:bg-emerald-600',
  }[st] || '');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/inductions/${inductionId}`, { headers: getAuthHeader() });
    if (r.ok) {
      setInd(await r.json());
      const fr = await fetch(`${API}/inductions/${inductionId}/feedback`, { headers: getAuthHeader() });
      if (fr.ok) setFeedback(await fr.json());
    } else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Not found'); }
    setLoading(false);
  }, [inductionId, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isHR) return;
    (async () => {
      const r = await fetch(`${API}/inductions/meta`, { headers: getAuthHeader() });
      if (r.ok) { const d = await r.json(); setOwnerCandidates(d.employees || []); }
    })();
  }, [isHR, getAuthHeader]);

  const publish = async () => {
    const r = await fetch(`${API}/inductions/${inductionId}/publish`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { const d = await r.json(); toast.success(`Sent for approval${d.owner_emails_sent ? ` · ${d.owner_emails_sent} owner email(s)` : ''}`); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Publish failed'); }
  };
  const completeSession = async (sid) => {
    const r = await fetch(`${API}/inductions/${inductionId}/sessions/${sid}/complete`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { toast.success('Session marked completed — feedback is now open for joiners'); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };
  const confirmConduct = async (sid, status, reason = '') => {
    if (status === 'Not Conducted' && !reason.trim()) { toast.error('Reason is required'); return; }
    const r = await fetch(`${API}/inductions/${inductionId}/sessions/${sid}/conduct`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason }),
    });
    if (r.ok) { toast.success(status === 'Conducted' ? 'Confirmed — feedback released to joiners' : 'Marked Not Conducted'); setConductNc(null); setNcText(''); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };
  const openReschedule = (s) => {
    setRForm({ date: s.date ? String(s.date).slice(0, 10) : '', start_time: s.start_time || '', end_time: s.end_time || '',
      owner_id: s.owner_id || '', owner_name: s.owner_name || '', owner_email: s.owner_email || '' });
    setResch(s);
  };
  const submitReschedule = async () => {
    if (!rForm.date || !rForm.start_time) { toast.error('Please set a new date and start time'); return; }
    const r = await fetch(`${API}/inductions/${inductionId}/sessions/${resch.session_id}/reschedule`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(rForm),
    });
    if (r.ok) { toast.success('Rescheduled — the owner has been notified to confirm the new time'); setResch(null); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Reschedule failed'); }
  };
  const downloadPdf = async () => {
    const r = await fetch(`${API}/inductions/${inductionId}/pdf`, { headers: getAuthHeader() });
    if (!r.ok) { toast.error('PDF export failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `Induction_${inductionId}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadFeedback = async (f) => {
    if (!f) return;
    const r = await fetch(`${API}/inductions/${inductionId}/feedback/${f.feedback_id}/pdf`, { headers: getAuthHeader() });
    if (!r.ok) { toast.error('Feedback download failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `Feedback_${f.feedback_id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Layout><div className="p-8 text-muted-foreground">Loading...</div></Layout>;
  if (!ind) return <Layout><div className="p-8">Induction not found.</div></Layout>;

  const isSuperAdmin = employee?.role === 'Super Admin';
  const attendeeCount = (s) => (s.attendee_ids && s.attendee_ids.length) ? s.attendee_ids.length : (ind.employees || []).length;
  const joinersWithStatus = (s) => {
    if (!s) return [];
    const empMap = Object.fromEntries((ind.employees || []).map(e => [e.employee_id, e]));
    const ids = (s.attendee_ids && s.attendee_ids.length) ? s.attendee_ids : (ind.employees || []).map(e => e.employee_id);
    const attMode = Object.fromEntries((s.attendees || []).map(a => [a.employee_id, a.mode]));
    const submitted = new Set(feedback.filter(f => f.session_id === s.session_id).map(f => f.employee_id));
    return ids.map(id => ({ employee_id: id, name: empMap[id]?.full_name || id, mode: attMode[id] || '', submitted: submitted.has(id) }));
  };
  const durationLabel = (s) => {
    if (!s.start_time || !s.end_time) return '';
    const [h1, m1] = s.start_time.split(':').map(Number);
    const [h2, m2] = s.end_time.split(':').map(Number);
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (!mins || mins <= 0) return '';
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h ? h + 'h ' : ''}${m ? m + 'm' : ''}`.trim();
  };
  const groups = [];
  (ind.sessions || []).forEach(s => {
    const key = (s.phase || '').trim() || 'Sessions';
    let g = groups.find(x => x.key === key);
    if (!g) { g = { key, items: [] }; groups.push(g); }
    g.items.push(s);
  });

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="induction-detail">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/inductions')} data-testid="detail-back-btn"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><GraduationCap className="h-6 w-6" /> {ind.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-xs text-muted-foreground">{ind.induction_id}</span>
                <Badge className={indStatusClass(ind.status)}>{ind.status}</Badge>
                {ind.joining_date && <span className="text-xs text-muted-foreground">Joining: {fmtDate(ind.joining_date)}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadPdf} data-testid="detail-pdf-btn"><FileDown className="h-4 w-4 mr-1.5" /> PDF</Button>
            {isHR && <Button variant="outline" onClick={() => navigate(`/inductions/${ind.induction_id}/edit`)} data-testid="detail-edit-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>}
            {isHR && ind.status === 'Draft' && <Button onClick={publish} data-testid="detail-publish-btn"><Send className="h-4 w-4 mr-1.5" /> Publish</Button>}
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> New Joiners ({(ind.employees || []).length})</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(ind.employees || []).map(e => <Badge key={e.employee_id} variant="secondary" className="py-1">{e.full_name} · {e.department}</Badge>)}
              {(ind.employees || []).length === 0 && <p className="text-sm text-muted-foreground">No joiners assigned.</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Schedule ({(ind.sessions || []).length} sessions)</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            {groups.map((g) => (
              <div key={g.key} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Flag className="h-4 w-4 text-primary" />
                  <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">{g.key}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                {g.items.map((s) => {
                  const idx = (ind.sessions || []).indexOf(s);
                  return (
                    <div key={s.session_id} className="border rounded-xl p-4 flex items-center gap-4" data-testid={`detail-session-${idx}`}>
                      <div className="text-center shrink-0 w-20">
                        <p className="text-sm font-semibold">{s.start_time ? to12h(s.start_time) : '--:--'}</p>
                        {durationLabel(s) && <p className="text-xs text-muted-foreground">{durationLabel(s)}</p>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{s.name}</p>
                        <div className="flex items-center gap-2 flex-wrap mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {s.owner_name || 'No owner'}</span>
                          <span className="px-2 py-0.5 rounded-full bg-muted text-foreground">{s.mode === 'Online' ? 'Online' : s.mode === 'Mixed' ? 'Mixed' : 'In-person'}</span>
                          <button type="button" onClick={() => setViewJoiners(s)} className="flex items-center gap-1 hover:text-foreground underline-offset-2 hover:underline" data-testid={`joiners-count-${idx}`}><Users className="h-3.5 w-3.5" /> {attendeeCount(s)}</button>
                          {(s.online_url || s.meeting_url) && (s.mode === 'Online' || s.mode === 'Mixed') && <a href={s.online_url || s.meeting_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><Video className="h-3.5 w-3.5" /> Link</a>}
                          {s.mode !== 'Online' && s.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {s.location}</span>}
                        </div>
                        {(s.reject_reason || s.suggested_time) && (
                          <div className="mt-2 text-xs rounded-md bg-muted/60 p-2 space-y-0.5" data-testid={`reschedule-details-${idx}`}>
                            {s.reject_reason && <div><span className="font-medium text-foreground">Reason:</span> <span className="text-rose-600">{s.reject_reason}</span></div>}
                            {s.suggested_time && <div><span className="font-medium text-foreground">Suggested:</span> <span className="text-amber-600">{fmtSuggested(s.suggested_time)}</span></div>}
                          </div>
                        )}
                        {s.conduct_status === 'Not Conducted' && s.not_conducted_reason && (
                          <div className="mt-2 text-xs rounded-md bg-rose-50 dark:bg-rose-900/20 p-2" data-testid={`notconducted-reason-${idx}`}><span className="font-medium">Not Conducted:</span> {s.not_conducted_reason}</div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge className={statusClass(sessionStatus(s))}>{sessionStatus(s)}</Badge>
                        {isHR && (['Rejected', 'Reschedule Requested'].includes(s.approval_status) || s.conduct_status === 'Not Conducted') && (
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openReschedule(s)} data-testid={`reschedule-session-${idx}`}><CalendarClock className="h-3.5 w-3.5 mr-1" /> Reschedule</Button>
                        )}
                        {isSuperAdmin && s.approval_status === 'Accepted' && !s.conduct_status && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="text-xs h-7 text-emerald-600" onClick={() => confirmConduct(s.session_id, 'Conducted')} data-testid={`conducted-session-${idx}`}>Conducted</Button>
                            <Button size="sm" variant="ghost" className="text-xs h-7 text-rose-600" onClick={() => { setNcText(''); setConductNc(s); }} data-testid={`notconducted-session-${idx}`}>Not Conducted</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </CardContent>
        </Card>

        {canViewFeedback && (
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Star className="h-4 w-4" /> Feedback ({feedback.length})</CardTitle></CardHeader>
            <CardContent>
              {feedback.length === 0 ? <p className="text-sm text-muted-foreground">No feedback submitted yet.</p> : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Joiner</TableHead><TableHead>Session</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {feedback.map(f => (
                        <TableRow key={f.feedback_id} data-testid={`feedback-row-${f.feedback_id}`}>
                          <TableCell>{f.employee_name}</TableCell>
                          <TableCell>{f.session_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{f.created_at ? new Date(f.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setViewFb(f)} data-testid={`feedback-view-${f.feedback_id}`}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => downloadFeedback(f)} data-testid={`feedback-download-${f.feedback_id}`}><Download className="h-3.5 w-3.5 mr-1" /> Download</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={!!conductNc} onOpenChange={(o) => !o && setConductNc(null)}>
        <DialogContent className="max-w-md" data-testid="conduct-nc-dialog">
          <DialogHeader><DialogTitle>Session Not Conducted — {conductNc?.name}</DialogTitle><DialogDescription>Record why this session did not take place. No feedback form will be sent.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Reason Session Was Not Conducted *</Label>
            <Textarea value={ncText} onChange={e => setNcText(e.target.value)} data-testid="conduct-nc-reason" placeholder="Why did the session not take place?" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConductNc(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => confirmConduct(conductNc.session_id, 'Not Conducted', ncText)} data-testid="conduct-nc-submit">Confirm Not Conducted</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewJoiners} onOpenChange={(o) => !o && setViewJoiners(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="joiners-dialog">
          <DialogHeader><DialogTitle>Joiners — {viewJoiners?.name}</DialogTitle><DialogDescription>Feedback submission status for each joiner of this session.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {joinersWithStatus(viewJoiners).map(j => (
              <div key={j.employee_id} className="flex items-center justify-between border rounded-md p-2" data-testid={`joiner-status-${j.employee_id}`}>
                <span className="text-sm">{j.name}{j.mode ? ` · ${j.mode}` : ''}</span>
                {j.submitted
                  ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Feedback submitted</span>
                  : <span className="text-xs text-muted-foreground flex items-center gap-1"><Circle className="h-3.5 w-3.5" /> Not submitted</span>}
              </div>
            ))}
            {joinersWithStatus(viewJoiners).length === 0 && <p className="text-sm text-muted-foreground">No joiners.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewFb} onOpenChange={(o) => !o && setViewFb(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="feedback-view-dialog">
          <DialogHeader><DialogTitle>Feedback — {viewFb?.session_name}</DialogTitle><DialogDescription>Submitted responses for this session.</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground">{viewFb?.employee_name}{viewFb?.template_name ? ` · ${viewFb.template_name}` : ''}{viewFb?.created_at ? ` · ${new Date(viewFb.created_at).toLocaleString('en-IN')}` : ''}</p>
          <div className="space-y-4 mt-2">
            {(viewFb?.qa || []).length === 0 && <p className="text-sm text-muted-foreground">No questions recorded.</p>}
            {(() => { let last = null; return (viewFb?.qa || []).map((q, i) => {
              const showSec = q.section && q.section !== last;
              last = q.section || last;
              return (
                <div key={i} data-testid={`fb-qa-${i}`}>
                  {showSec && <p className="text-xs font-bold uppercase tracking-wide text-primary mt-3 mb-1">{q.section}</p>}
                  <p className="text-sm font-medium">{i + 1}. {q.label}</p>
                  <p className="text-sm text-muted-foreground">{q.type === 'rating' && q.answer !== '—' ? `${q.answer}/${q.scale || 5}` : q.type === 'likert' && q.answer !== '—' ? `${q.answer}/5${q.answer_label ? ` – ${q.answer_label}` : ''}` : q.answer}</p>
                </div>
              );
            }); })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadFeedback(viewFb)} data-testid="feedback-view-download"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resch} onOpenChange={(o) => !o && setResch(null)}>
        <DialogContent className="max-w-md" data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Reschedule — {resch?.name}</DialogTitle><DialogDescription>Set a new time and (optionally) reassign the session owner. The owner will be notified to Accept, Reject or Suggest a new time.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {resch?.reject_reason && <p className="text-sm text-rose-600">Owner's reason: {resch.reject_reason}</p>}
            {resch?.suggested_time && <p className="text-sm text-amber-600">Owner suggested: {resch.suggested_time}</p>}
            {resch?.conduct_status === 'Not Conducted' && resch?.not_conducted_reason && <p className="text-sm text-rose-600">Not conducted: {resch.not_conducted_reason}</p>}
            <div>
              <Label>Session Owner</Label>
              <Popover open={ownerPickerOpen} onOpenChange={setOwnerPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" data-testid="reschedule-owner" className="w-full justify-between font-normal mt-1.5">
                    <span className="truncate">{rForm.owner_name || 'Select owner...'}</span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search employee..." />
                    <CommandList>
                      <CommandEmpty>No employee found.</CommandEmpty>
                      <CommandGroup>
                        {ownerCandidates.map(e => (
                          <CommandItem key={e.employee_id} value={`${e.full_name} ${e.email} ${e.department}`}
                            onSelect={() => { setRForm({ ...rForm, owner_id: e.employee_id, owner_name: e.full_name, owner_email: e.email }); setOwnerPickerOpen(false); }}
                            data-testid={`reschedule-owner-opt-${e.employee_id}`}>
                            <Check className={`mr-2 h-4 w-4 ${rForm.owner_id === e.employee_id ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="min-w-0">
                              <p className="truncate text-sm">{e.full_name}</p>
                              <p className="truncate text-xs text-muted-foreground">{e.department}</p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>New Date *</Label>
              <Input type="date" value={rForm.date} onChange={e => setRForm({ ...rForm, date: e.target.value })} data-testid="reschedule-date" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start *</Label>
                <div className="mt-1.5"><TimePicker12 value={rForm.start_time} onChange={v => setRForm({ ...rForm, start_time: v })} testid="reschedule-start" /></div>
              </div>
              <div>
                <Label>End</Label>
                <div className="mt-1.5"><TimePicker12 value={rForm.end_time} onChange={v => setRForm({ ...rForm, end_time: v })} testid="reschedule-end" /></div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">The session owner will be notified again (Help Desk + email) to Accept, Reject or Suggest a new time.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResch(null)}>Cancel</Button>
            <Button onClick={submitReschedule} data-testid="reschedule-submit">Reschedule & Notify Owner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
